import {Button, Toolbar} from "@progress/kendo-react-buttons"
import {Field, Form, FormClassComponent, FormSubmitClickEvent} from "@progress/kendo-react-form"
import {Input, NumericTextBox} from "@progress/kendo-react-inputs"
import {
    MDBBtn,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
} from "mdbreact"
import {createRef} from "react"
import {useDispatch} from "react-redux"
import {setToastr} from "../../containers/App/appSlice"
import {
    CustomPrice,
    CustomPricingTestPackage,
    CustomTestPackage,
    postCustomPriceDetails,
    setSelectedCustomPricingId,
    showManageCustomPricingModal,
} from "../../containers/CustomPrices/customPricesSlice"
import {RootState} from "../../rootReducer"
import {SpinnerFragment} from "../../shared/CommonItems"
import {useAppSelector} from "../../store"
import {PermissionsChecker, hasPermission} from "../Shared/permissionChecker"
import {requiredValidator} from "../Shared/validators"
import ManageCustomPriceTestPackages from "./manageCustomPriceTestPackages"

const AddEditCustomPrice = (): JSX.Element => {
    const dispatch = useDispatch()

    const isModalLoading = useAppSelector((state: RootState) => state.containers.customPrices.isModalLoading)
    const selectedCustomPrice = useAppSelector((state: RootState) => state.containers.customPrices.selectedCustomPrice)
    const showManageCustomPricing = useAppSelector(
        (state: RootState) => state.containers.customPrices.showManageCustomPricing,
    )
    const cpTestPackages = useAppSelector((state: RootState) => state.containers.customPrices.customPriceTestPackages)

    const permissions = useAppSelector((state: RootState) => state.application.permissions)
    const verifyPermission = hasPermission(["CustomPricing.Edit"], permissions)
    const formEl = createRef<FormClassComponent>()
    const selectedRegion = useAppSelector((state: RootState) => state.application.selectedRegion)

    const toggleToastr = (message: string) => {
        dispatch(
            setToastr({
                isActive: true,
                message,
                delay: 3000,
                title: "Error",
                type: "danger",
                description: "",
            }),
        )
    }

    const handleSubmit = (e: FormSubmitClickEvent) => {
        const newCPtestPackage = cpTestPackages
            .filter((customTestPackage: CustomTestPackage) =>
                customTestPackage.customPricingTestPackages.some(
                    (customPricingTestPackage: CustomPricingTestPackage) => customPricingTestPackage.selected === true,
                ),
            )
            .map((customTestPackage: CustomTestPackage) =>
                customTestPackage.customPricingTestPackages.filter(
                    (customPricingTestPackage: CustomPricingTestPackage) => customPricingTestPackage.selected === true,
                ),
            )

        if (e.isValid) {
            const customprice = {...e.values} as CustomPrice
            const isLFRangeValid = ![
                customprice.monday,
                customprice.tuesday,
                customprice.wednesday,
                customprice.thursday,
                customprice.friday,
                customprice.saturday,
                customprice.sunday,
            ].every(price => (price === null ? 1 : price > 0 && price < 100))

            if (customprice.customPricingName.trim().length === 0) {toggleToastr("Pricing name cannot be empty")}
            else if (selectCustomPrice(customprice) === null)
               { toggleToastr(
                    isLFRangeValid
                        ? "Lane fees must be greater 0 and less than 100"
                        : "Please enter atleast 1 lane Fee",
                )}
            else if (isLFRangeValid) {toggleToastr("Lane fees must be between 0 and 100")}
            else if (newCPtestPackage.length === 0) {toggleToastr("Please select a test package")}
            else {
                customprice.customPricingName = customprice.customPricingName.trim()
                customprice.pricingTestPackages = newCPtestPackage
                    .map((cp: any) => cp.map((ctp: any) => `${ctp.testPackageID}~${ctp.dealerID}`))
                    .flat()
                dispatch(postCustomPriceDetails(selectedRegion?.regionId ?? 0, customprice))
            }
        }
    }

    const selectCustomPrice=(customprice: CustomPrice)=>customprice.monday || customprice.tuesday || customprice.wednesday || customprice.thursday || customprice.friday || customprice.saturday || customprice.sunday;

    return (
        <MDBModal staticBackdrop animationDirection="right" show={showManageCustomPricing} tabIndex="-1">
            <MDBModalDialog position="top right modal-xl" side>
                <MDBModalContent className="full-height">
                    <MDBModalHeader>
                        <MDBModalTitle>
                            Custom Price {selectedRegion && <>for {selectedRegion.regionName}</>}
                        </MDBModalTitle>
                        <MDBBtn
                            color="none"
                            className="btn-close"
                            onClick={() => {
                                dispatch(showManageCustomPricingModal(false))
                                dispatch(setSelectedCustomPricingId(0))
                            }}
                        ></MDBBtn>
                    </MDBModalHeader>
                    <PermissionsChecker permission={["CustomPricing.Edit"]}>
                        <Toolbar className="shadow-0 border-bottom p-0 pb-1 pt-1">
                            <Button
                                fillMode="flat"
                                themeColor={"primary"}
                                iconClass="fa fa-save"
                                onClick={e => formEl.current && formEl.current.onSubmit(e)}
                                title="Save"
                                disabled={
                                    !cpTestPackages ||
                                    isModalLoading ||
                                    selectedCustomPrice === null ||
                                    !verifyPermission
                                }
                            >
                                Save
                            </Button>
                        </Toolbar>
                    </PermissionsChecker>
                    {(!cpTestPackages || isModalLoading || selectedCustomPrice === null) && <SpinnerFragment />}
                    <MDBModalBody>
                        <fieldset disabled={verifyPermission ? false : true}>
                            <Form
                                ref={formEl}
                                key={JSON.stringify(selectedCustomPrice)}
                                initialValues={selectedCustomPrice ?? {}}
                                onSubmitClick={handleSubmit}
                                render={() => (
                                    <>
                                        <Field
                                            name="customPricingName"
                                            id="customPricingName"
                                            component={Input}
                                            label={"Custom pricing name"}
                                            validator={requiredValidator}
                                        />
                                        <div className="mt-3 pb-1 mb-2 text-muted" style={{fontWeight: 700}}>
                                            Lane Fees
                                        </div>
                                        <MDBRow className="mb-2">
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="monday"
                                                    id="monday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Monday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="tuesday"
                                                    id="tuesday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Tuesday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="wednesday"
                                                    id="wednesday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Wednesday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                        </MDBRow>
                                        <MDBRow className="mb-2">
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="thursday"
                                                    id="thursday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Thursday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="friday"
                                                    id="friday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Friday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="saturday"
                                                    id="saturday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Saturday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                        </MDBRow>
                                        <MDBRow className="mb-3">
                                            <MDBCol md="4">
                                                <Field
                                                    spinners={false}
                                                    name="sunday"
                                                    id="sunday"
                                                    component={NumericTextBox}
                                                    format="c2"
                                                    label="Sunday"
                                                    min={0}
                                                />
                                            </MDBCol>
                                            <MDBCol md="4"></MDBCol>
                                        </MDBRow>
                                    </>
                                )}
                            />
                            <ManageCustomPriceTestPackages />
                        </fieldset>
                    </MDBModalBody>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    )
}

export default AddEditCustomPrice
